// 扑克牌类型定义

export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';
export type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';

export interface Card {
  id: string;
  suit: Suit;
  rank: Rank;
  value: number; // 2-14 (A=14)
}

export type PositionTag = 'BTN' | 'SB' | 'BB' | 'UTG' | 'UTG+1' | 'MP' | 'MP+1' | 'CO' | 'EP' | 'LP';

export type GamePhase = 
  | 'waiting'      // 等待开始
  | 'preflop'      // 翻牌前
  | 'flop'         // 翻牌
  | 'turn'         // 转牌
  | 'river'        // 河牌
  | 'showdown'     // 摊牌
  | 'settlement';  // 结算

export type PlayerStatus = 'active' | 'folded' | 'all-in' | 'eliminated';

export type PlayerActionType = 'fold' | 'check' | 'call' | 'raise' | 'all-in';

export interface PlayerAction {
  type: PlayerActionType;
  amount?: number;
}

export interface SeatPlayer {
  id: string;
  name: string;
  isHuman: boolean;
  aiLevel?: 'easy' | 'medium' | 'hard';
  chips: number;
  holeCards: [Card, Card] | null;
  currentBet: number;
  totalBetThisRound: number;
  status: PlayerStatus;
  position: PositionTag;
  avatar: string;
}

export interface Pot {
  id: number;
  amount: number;
  eligiblePlayers: string[]; // 玩家ID列表
}

export interface GameState {
  phase: GamePhase;
  deck: Card[];
  communityCards: Card[];
  players: SeatPlayer[];
  dealerIndex: number;
  currentPlayerIndex: number;
  pots: Pot[];
  currentBet: number;
  minRaise: number;
  blinds: { small: number; big: number };
  handNumber: number;
  mode: GameMode;
  timestamp: number;
}

export type GameMode = 'casual' | 'tournament_low' | 'tournament_mid' | 'tournament_high' | 'turbo';

export interface GameConfig {
  mode: GameMode;
  smallBlind: number;
  bigBlind: number;
  entryFee: number;
  maxPlayers: number;
  minPlayers: number;
  blindIncreaseInterval?: number; // 分钟，锦标赛用
  startingChips?: number; // 锦标赛起始筹码
}

// 牌型等级（从高到低）
export const HandRank = {
  ROYAL_FLUSH: 10,
  STRAIGHT_FLUSH: 9,
  FOUR_OF_A_KIND: 8,
  FULL_HOUSE: 7,
  FLUSH: 6,
  STRAIGHT: 5,
  THREE_OF_A_KIND: 4,
  TWO_PAIR: 3,
  ONE_PAIR: 2,
  HIGH_CARD: 1,
} as const;

export type HandRank = typeof HandRank[keyof typeof HandRank];

export interface HandEvaluation {
  rank: HandRank;
  name: string;
  cards: Card[]; // 组成牌型的5张牌
  kickers: Card[]; // 踢脚牌
  value: number; // 用于比较大小
}

export interface ShowdownResult {
  winners: { playerId: string; potId: number; amount: number }[];
  handEvaluations: { playerId: string; evaluation: HandEvaluation }[];
}

export interface SettlementResult {
  chipChanges: { playerId: string; change: number }[];
  scoreChanges: { playerId: string; change: number }[];
}

// 玩家档案
export interface PlayerProfile {
  id: string;
  name: string;
  avatar: string;
  chips: number;
  score: number;
  createdAt: number;
  lastLoginDate: string;
  dailyReliefCount: number;
  stats: PlayerStats;
}

export interface PlayerStats {
  totalGames: number;
  wins: number;
  winRate: number;
  biggestPot: number;
  currentStreak: number;
  bestStreak: number;
  royalFlushCount: number;
  bluffSuccessCount: number;
  totalHandsPlayed: number;
  vpip: number; // Voluntarily Put $ In Pot
  pfr: number; // Pre-Flop Raise
}

export interface GameRecord {
  id: string;
  profileId: string;
  mode: GameMode;
  placement: number;
  chipChange: number;
  scoreChange: number;
  handsPlayed: number;
  timestamp: number;
  highlights: HandHighlight[];
}

export interface HandHighlight {
  handId: number;
  playerCards: [Card, Card];
  communityCards: Card[];
  potSize: number;
  result: 'royal_flush' | 'straight_flush' | 'four_kind' | 'bluff_success' | 'big_pot' | 'win';
}

export interface LeaderboardEntry {
  id: string;
  profileId: string;
  name: string;
  avatar: string;
  score: number;
  weekKey: string;
  rank: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  condition: string;
  unlockedAt?: number;
}

export interface DailyChallenge {
  id: string;
  date: string;
  description: string;
  target: string;
  reward: number;
  completed: boolean;
}
